/**
 * Shared helpers for the Classroom Analysis Hub (regular classes).
 */

/**
 * Format a Unix timestamp (seconds) to YYYY-MM-DD in the local timezone.
 */
export const formatClassDate = timestamp => {
  if (!timestamp) return '-';
  const date = new Date(timestamp * 1000);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Format seconds into HH:MM:SS.
 */
export const formatSeconds = totalSeconds => {
  if (!totalSeconds) return '-';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
    seconds,
  ).padStart(2, '0')}`;
};

/**
 * Parse bucket and key from an S3 URL (supports both path-style and virtual-hosted).
 *   Path style:    https://s3-region.amazonaws.com/bucket/key
 *   Virtual-hosted: https://bucket.s3.region.amazonaws.com/key
 * Returns { bucket, key } or null on failure.
 */
export const parseBucketKey = s3Url => {
  try {
    const url = new URL(s3Url);
    if (url.hostname.startsWith('s3')) {
      const parts = url.pathname.split('/').filter(p => p !== '');
      return { bucket: parts[0], key: parts.slice(1).join('/') };
    }
    return {
      bucket: url.hostname.split('.s3')[0],
      key: url.pathname.replace(/^\//, ''),
    };
  } catch {
    return null;
  }
};

/**
 * Extract the raw S3 video URL from a log_url (compose endpoint URL).
 * log_url format: https://admin.cuemath.com/communication/admin/video-view/?call_type=third_party&videoUrl=<encoded_s3_url>
 */
export const extractVideoUrlFromLogUrl = logUrl => {
  if (!logUrl || !logUrl.includes('videoUrl=')) return null;
  try {
    return decodeURIComponent(logUrl.split('videoUrl=')[1]);
  } catch {
    return null;
  }
};

/**
 * Build an s3:// URI from an HTTPS S3 URL (for the transcribe endpoint).
 */
export const buildS3Uri = httpsUrl => {
  try {
    const url = new URL(httpsUrl);
    if (url.hostname.startsWith('s3')) {
      return 's3:/' + url.pathname;
    }
    return 's3://' + url.hostname.split('.s3')[0] + url.pathname;
  } catch {
    return null;
  }
};

/**
 * Safely parse JSON from sessionStorage. Returns null on failure.
 */
export const safeSessionGet = key => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Unwrap an API gateway response. The gateway may wrap as { value: [payload, statusCode] }
 * or { value: payload }.
 */
const unwrapGateway = res => {
  const raw = res?.value || res;
  return Array.isArray(raw) ? raw[0] : raw;
};

/**
 * Merge classroom + summary data into a flat object for display.
 * Used by both RegularClassTable (search result) and ClassroomViewPage (direct URL).
 */
export const mergeClassroomSummary = (classroomRes, summaryRes, teacherClassroomId) => {
  const classroomData = unwrapGateway(classroomRes);
  const summaryData = unwrapGateway(summaryRes);

  const classData = summaryData.class_data || {};
  const studentLogs = summaryData.student_call_logs || [];
  const firstStudent = studentLogs[0] || {};
  const groupDuration = summaryData.group_call_logs?.group_logs_duration || 0;
  const studentDuration = summaryData.call_logs_duration || 0;
  const groupLogs = summaryData.group_call_logs?.group_logs || [];
  const firstGroupLog = groupLogs[0] || {};

  return {
    ...classroomData,
    teacher_classroom_id: teacherClassroomId || classroomData.id,
    teacher_name: classData.teacher_name || classroomData.teacher_name || '-',
    student_name: firstStudent.student_name || '-',
    student_grade: firstStudent.student_grade || classroomData.grade || '-',
    call_duration: groupDuration + studentDuration,
    log_url: firstGroupLog.log_url || null,
  };
};
